import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { assistantChat, getAssistantHistory, getClients, createJob, createCampaign } from "./opsApi";
import { useOpsAuth } from "./OpsAuthContext";
import { Send, Sparkles, Upload, Check, X, Film, Megaphone } from "lucide-react";

const inp = "w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-[#AC3333]";

function ReelCard({ card, clients, onDone }) {
  const [f, setF] = useState({
    client_id: clients.find((c) => c.name?.toLowerCase() === (card.cliente || "").toLowerCase())?.id || "",
    artista: card.artista || "", lanzamiento: card.lanzamiento || "", inicio: card.inicio || "00:00:00",
    duracion: Number(card.duracion) || 15,
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    if (!f.client_id) { toast.error("Elige el cliente"); return; }
    if (!file) { toast.error("Adjunta el MP3 del tema"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("El MP3 supera 15 MB"); return; }
    const fd = new FormData();
    fd.append("client_id", f.client_id); fd.append("artista", f.artista); fd.append("lanzamiento", f.lanzamiento);
    fd.append("inicio", f.inicio); fd.append("duracion", String(f.duracion)); fd.append("audio", file);
    setBusy(true);
    try {
      const job = await createJob(fd);
      toast[job.status === "error" ? "error" : "success"](job.status === "error" ? (job.mensaje || "Error en n8n") : "¡Reel enviado a producción!");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="reel-card" className="mt-2 rounded-xl border border-[#AC3333]/40 bg-[#AC3333]/5 p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold text-white"><Film className="h-4 w-4 text-[#AC3333]" /> Confirmar: Generar Reel</div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div><label className="text-[11px] text-white/50">Cliente</label><select data-testid="reel-client" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} className={inp}><option value="">—</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
        <div><label className="text-[11px] text-white/50">Artista</label><input value={f.artista} onChange={(e) => setF({ ...f, artista: e.target.value })} className={inp} /></div>
        <div><label className="text-[11px] text-white/50">Lanzamiento</label><input value={f.lanzamiento} onChange={(e) => setF({ ...f, lanzamiento: e.target.value })} className={inp} /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-[11px] text-white/50">Inicio</label><input value={f.inicio} onChange={(e) => setF({ ...f, inicio: e.target.value.replace(/[^\d:]/g, "").slice(0, 8) })} className={`${inp} font-mono`} /></div>
          <div><label className="text-[11px] text-white/50">Dur. (s)</label><input type="number" min={5} max={90} value={f.duracion} onChange={(e) => setF({ ...f, duracion: Number(e.target.value) })} className={inp} /></div>
        </div>
      </div>
      <label data-testid="reel-file" className="mt-2 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/20 px-3 py-2 text-xs text-white/60 hover:border-[#AC3333]"><Upload className="h-4 w-4" /> {file ? file.name : "Adjunta el MP3 (máx. 15 MB)"}<input type="file" accept="audio/mpeg,audio/mp3" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} /></label>
      <div className="mt-3 flex gap-2">
        <button data-testid="reel-approve" onClick={approve} disabled={busy} className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"><Check className="h-4 w-4" /> {busy ? "Enviando…" : "Aprobar y generar"}</button>
        <button data-testid="reel-cancel" onClick={onDone} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-sm text-white/70"><X className="h-4 w-4" /> Descartar</button>
      </div>
    </div>
  );
}

function CampaignCard({ card, clients, onDone }) {
  const [f, setF] = useState({
    client_id: clients.find((c) => c.name?.toLowerCase() === (card.cliente || "").toLowerCase())?.id || "",
    objetivo: card.objetivo || "", video_url: card.video_url || "", segmentacion: card.segmentacion || "", presupuesto: card.presupuesto || "",
  });
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    if (!f.client_id) { toast.error("Elige el cliente"); return; }
    if (!f.video_url.trim()) { toast.error("Falta el enlace del video de la pauta"); return; }
    setBusy(true);
    try {
      const c = await createCampaign({ client_id: f.client_id, kind: "YouTube Ads", objetivo: f.objetivo, video_url: f.video_url, segmentacion: f.segmentacion, presupuesto: f.presupuesto });
      toast[c.status === "error" ? "error" : "success"](c.status === "error" ? (c.mensaje || "Error en n8n") : "¡Campaña enviada!");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="campaign-card" className="mt-2 rounded-xl border border-[#AC3333]/40 bg-[#AC3333]/5 p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold text-white"><Megaphone className="h-4 w-4 text-[#AC3333]" /> Confirmar: Campaña YouTube Ads</div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div><label className="text-[11px] text-white/50">Cliente</label><select data-testid="camp-client" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} className={inp}><option value="">—</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
        <div><label className="text-[11px] text-white/50">Objetivo</label><input data-testid="camp-objetivo" value={f.objetivo} onChange={(e) => setF({ ...f, objetivo: e.target.value })} className={inp} placeholder="Ej. 5.000 reproducciones" /></div>
        <div className="sm:col-span-2"><label className="text-[11px] text-white/50">Video de la pauta (URL)</label><input data-testid="camp-video" value={f.video_url} onChange={(e) => setF({ ...f, video_url: e.target.value })} className={inp} placeholder="https://youtube.com/watch?v=..." /></div>
        <div><label className="text-[11px] text-white/50">Segmentación</label><input data-testid="camp-seg" value={f.segmentacion} onChange={(e) => setF({ ...f, segmentacion: e.target.value })} className={inp} placeholder="Caracas, Maracaibo, Valencia" /></div>
        <div><label className="text-[11px] text-white/50">Presupuesto (opcional)</label><input data-testid="camp-presupuesto" value={f.presupuesto} onChange={(e) => setF({ ...f, presupuesto: e.target.value })} className={inp} placeholder="$" /></div>
      </div>
      <div className="mt-3 flex gap-2">
        <button data-testid="camp-approve" onClick={approve} disabled={busy} className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"><Check className="h-4 w-4" /> {busy ? "Enviando…" : "Aprobar y lanzar"}</button>
        <button data-testid="camp-cancel" onClick={onDone} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-sm text-white/70"><X className="h-4 w-4" /> Descartar</button>
      </div>
    </div>
  );
}

export default function OpsChat() {
  const { user } = useOpsAuth();
  const sid = `ops-${user?.id || "main"}`;
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    getAssistantHistory(sid).then(setMsgs).catch(() => {});
    getClients().then(setClients).catch(() => {});
  }, [sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async () => {
    const m = text.trim();
    if (!m || busy) return;
    setText("");
    setMsgs((p) => [...p, { role: "user", text: m }]);
    setBusy(true);
    try {
      const r = await assistantChat(sid, m);
      setMsgs((p) => [...p, { role: "assistant", text: r.reply, card: r.card }]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error del asistente"); }
    finally { setBusy(false); }
  };
  const dismissCard = (idx) => setMsgs((p) => p.map((x, i) => (i === idx ? { ...x, card: null } : x)));

  return (
    <div data-testid="ops-chat" className="mx-auto flex h-[calc(100vh-4rem)] max-w-3xl flex-col">
      <div className="mb-3 flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#AC3333]" /><h2 className="font-serif text-xl font-bold text-white">Asistente de Operaciones</h2></div>
      <div className="flex-1 space-y-4 overflow-y-auto rounded-2xl border border-white/10 bg-[#241010] p-4">
        {msgs.length === 0 && (
          <div className="mt-10 text-center text-white/40">
            <p className="font-serif text-lg text-white/70">¿Qué hacemos hoy?</p>
            <p className="mt-2 text-sm">Pídeme algo como: <em>"Genera un reel para el artista Rxssell, tema Noche, desde el segundo 00:00:30, 15s"</em></p>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-[#AC3333] text-white" : "bg-white/5 text-white/90"}`} data-testid={`ops-msg-${m.role}-${i}`}>
              <div className="whitespace-pre-wrap">{m.text}</div>
              {m.card && m.card.type === "youtube_ads" && <CampaignCard card={m.card} clients={clients} onDone={() => dismissCard(i)} />}
              {m.card && m.card.type !== "youtube_ads" && <ReelCard card={m.card} clients={clients} onDone={() => dismissCard(i)} />}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="rounded-2xl bg-white/5 px-4 py-2.5 text-sm text-white/40">Escribiendo…</div></div>}
        <div ref={endRef} />
      </div>
      <div className="mt-3 flex items-end gap-2">
        <textarea data-testid="ops-chat-input" rows={1} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Escribe tu orden… (Enter para enviar)" className="flex-1 resize-none rounded-xl border border-white/10 bg-[#241010] px-4 py-3 text-sm text-white outline-none focus:border-[#AC3333]" />
        <button data-testid="ops-chat-send" onClick={send} disabled={busy} className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#AC3333] text-white hover:bg-[#FF0505] disabled:opacity-60"><Send className="h-5 w-5" /></button>
      </div>
    </div>
  );
}
